import {
  Card,
  Table,
  Button,
  Input,
  DatePicker,
  Select,
  Space,
  Tag,
  Statistic,
  Row,
  Col,
  Modal,
  Form,
  InputNumber,
  Dropdown,
  Divider,
} from "antd";
import {
  SearchOutlined,
  PlusOutlined,
  DollarOutlined,
  CalendarOutlined,
  FileTextOutlined,
  DownloadOutlined,
  FilterOutlined,
  ShoppingCartOutlined,
  CarOutlined,
  TeamOutlined,
  ToolOutlined,
  MoreOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import PageMeta from "~/components/common/meta";
import { BillingSubNav } from "~/components/admin/billing";
import dayjs from "dayjs";
import {
  useGetExpenses,
  useGetExpenseStats,
} from "~/services/expense/expense.queries";
import {
  useCreateExpense,
  useDeleteExpense,
  useUpdateExpense,
  useCreateExpenseInvoice,
} from "~/services/expense/expense.mutations";
import {
  ExpenseCategory,
  ExpenseStatus,
  TExpense,
  CreateExpenseDto,
} from "~/types/expense";
import { useGetVendors } from "~/services/vendor/vendor.queries";
import { useGetProjects } from "~/services/project/project.queries";
import { useNotification } from "~/hooks/useNotification";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

const { RangePicker } = DatePicker;
const { Option } = Select;
const { TextArea } = Input;

const CATEGORY_OPTIONS = [
  {
    value: ExpenseCategory.SUPPLIES,
    label: "Supplies",
    icon: <ShoppingCartOutlined />,
  },
  {
    value: ExpenseCategory.TRANSPORTATION,
    label: "Transportation",
    icon: <CarOutlined />,
  },
  {
    value: ExpenseCategory.EQUIPMENT,
    label: "Equipment",
    icon: <ToolOutlined />,
  },
  {
    value: ExpenseCategory.OFFICE,
    label: "Office",
    icon: <FileTextOutlined />,
  },
  {
    value: ExpenseCategory.INSURANCE,
    label: "Insurance",
    icon: <TeamOutlined />,
  },
  { value: ExpenseCategory.MAINTENANCE, label: "Maintenance" },
  { value: ExpenseCategory.UTILITIES, label: "Utilities" },
  { value: ExpenseCategory.OTHER, label: "Other" },
];

const STATUS_OPTIONS = [
  { value: "all", label: "All Status" },
  { value: ExpenseStatus.PENDING, label: "Pending" },
  { value: ExpenseStatus.APPROVED, label: "Approved" },
  { value: ExpenseStatus.PAID, label: "Paid" },
  { value: ExpenseStatus.REJECTED, label: "Rejected" },
];

export const ScreenExpenses: React.FC = () => {
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [dateRange, setDateRange] = useState<any>(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [selectedExpense, setSelectedExpense] = useState<TExpense | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [isCreateInvoiceModalOpen, setIsCreateInvoiceModalOpen] =
    useState(false);
  const [invoiceForm] = Form.useForm();

  const notification = useNotification();
  const navigate = useNavigate();

  // Query params
  const params = useMemo(() => {
    const p: any = { page: 1, pageSize: 10 };
    if (searchText) p.search = searchText;
    if (statusFilter !== "all") p.status = statusFilter;
    if (categoryFilter !== "all") p.category = categoryFilter;
    if (dateRange && dateRange[0] && dateRange[1]) {
      p.startDate = dayjs(dateRange[0]).format("YYYY-MM-DD");
      p.endDate = dayjs(dateRange[1]).format("YYYY-MM-DD");
    }
    return p;
  }, [searchText, statusFilter, categoryFilter, dateRange]);

  // Data hooks
  const { data: expensesData, isLoading } = useGetExpenses(params);
  const { data: stats } = useGetExpenseStats();
  const createExpenseMutation = useCreateExpense();
  const { data: vendorsData } = useGetVendors({ page: 1, pageSize: 100 });
  const { data: projectsData } = useGetProjects({ page: 1, pageSize: 100 });
  const vendors = vendorsData?.items || [];
  const projects = projectsData?.items || [];
  const deleteExpenseMutation = useDeleteExpense();
  const updateExpenseMutation = useUpdateExpense();
  const createExpenseInvoiceMutation = useCreateExpenseInvoice();

  // Table columns
  const columns = [
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      render: (date: string) => (
        <div className="flex items-center">
          <CalendarOutlined className="mr-2 text-gray-400" />
          {dayjs(date).format("MMM DD, YYYY")}
        </div>
      ),
      sorter: (a: TExpense, b: TExpense) =>
        dayjs(a.date).unix() - dayjs(b.date).unix(),
    },
    {
      title: "Vendor",
      dataIndex: ["vendor", "name"],
      key: "vendor",
      render: (_: any, record: TExpense) => (
        <div className="font-medium text-gray-900">
          {record.vendor?.name || "-"}
        </div>
      ),
    },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
      render: (category: ExpenseCategory) => {
        const found = CATEGORY_OPTIONS.find((c) => c.value === category);
        return (
          <div className="flex items-center">
            <span className="mr-2 text-blue-500">{found?.icon}</span>
            {found?.label || category}
          </div>
        );
      },
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      render: (description: string, record: TExpense) => (
        <div>
          <div className="text-gray-900">{description}</div>
          {record.project?.name && (
            <div className="text-sm text-gray-500">
              Project: {record.project.name}
            </div>
          )}
        </div>
      ),
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      render: (amount: number) => (
        <div className="font-semibold text-lg text-gray-900">
          ${Number(amount).toFixed(2)}
        </div>
      ),
      sorter: (a: TExpense, b: TExpense) => a.amount - b.amount,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: ExpenseStatus) => {
        const statusConfig = {
          [ExpenseStatus.PENDING]: { color: "orange", label: "Pending" },
          [ExpenseStatus.APPROVED]: { color: "blue", label: "Approved" },
          [ExpenseStatus.PAID]: { color: "green", label: "Paid" },
          [ExpenseStatus.REJECTED]: { color: "red", label: "Rejected" },
        };
        const config = statusConfig[status];
        return <Tag color={config.color}>{config.label}</Tag>;
      },
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: any, record: TExpense) => {
        const menuItems = [
          {
            key: "view",
            label: "View Details",
            icon: <FileTextOutlined />,
            onClick: () => {
              setSelectedExpense(record);
              setIsDetailModalOpen(true);
            },
          },
          {
            key: "edit",
            label: "Edit",
            icon: <ToolOutlined />,
            onClick: () => {
              setSelectedExpense(record);
              setIsEditModalOpen(true);
            },
          },
          {
            key: "create-invoice",
            label: "Create Invoice",
            icon: <DollarOutlined />,
            onClick: () => handleOpenCreateInvoiceModal(record),
          },
        ];
        menuItems.push({
          key: "delete",
          label: "Delete",
          icon: <DeleteOutlined style={{ color: "red" }} />,
          onClick: () => {
            setSelectedExpense(record);
            setIsDeleteConfirmOpen(true);
          },
        });
        menuItems.push({
          key: "receipt",
          label: "Download Receipt",
          icon: <DownloadOutlined />,
          onClick: () => {
            if (record.receiptUrl) {
              window.open(record.receiptUrl, "_blank");
            }
          },
        });
        return (
          <Dropdown
            menu={{
              items: menuItems,
              onClick: () => {},
            }}
          >
            <Button type="text" icon={<MoreOutlined />} />
          </Dropdown>
        );
      },
    },
  ];

  // Handle create expense
  const handleCreateExpense = async (values: any) => {
    const payload: CreateExpenseDto = {
      date: values.date.format("YYYY-MM-DD"),
      amount: values.amount,
      category: values.category,
      description: values.description,
      vendorId: values.vendorId,
      projectId: values.projectId,
      notes: values.notes,
      // receiptUrl: ... (handle upload separately)
    };
    try {
      await createExpenseMutation.mutateAsync(payload);
      setIsModalVisible(false);
      form.resetFields();
      notification.success({ message: "Expense created successfully!" });
    } catch (err: any) {
      notification.error({
        message: err?.response?.data?.message || "Failed to create expense",
      });
    }
  };

  // Stats
  const totalExpenses = stats?.totalAmount ?? 0;
  const pendingExpenses = stats?.pendingAmount ?? 0;
  const approvedExpenses = stats?.approvedAmount ?? 0;
  const paidExpenses = stats?.paidAmount ?? 0;

  const generateInvoiceNumber = (expense: TExpense) => {
    const timestamp = Date.now().toString().slice(-6);
    const expenseRef = expense.expenseRef.replace("EXP-", "");
    return `INV-EXP-${expenseRef}-${timestamp}`.substring(0, 21);
  };

  const handleOpenCreateInvoiceModal = (expense: TExpense) => {
    setSelectedExpense(expense);
    setIsCreateInvoiceModalOpen(true);
    setTimeout(() => {
      invoiceForm.setFieldsValue({
        invoiceNumber: generateInvoiceNumber(expense),
        dueDate: dayjs().add(30, "days"),
        memo: `Expense: ${expense.expenseRef}${
          expense.project ? ` | Project: ${expense.project.name}` : ""
        }${expense.vendor ? ` | Vendor: ${expense.vendor.name}` : ""}`,
      });
    }, 100);
  };

  const handleCreateInvoice = async (values: any) => {
    if (!selectedExpense) return;
    try {
      const payload = {
        invoiceNumber: values.invoiceNumber,
        dueDate: values.dueDate?.format("YYYY-MM-DD"),
        memo: values.memo,
      };
      await createExpenseInvoiceMutation.mutateAsync({
        expenseId: selectedExpense.id,
        payload,
      });
      setIsCreateInvoiceModalOpen(false);
      invoiceForm.resetFields();
      notification.success({
        message: "Invoice created successfully in QuickBooks!",
      });
    } catch (err: any) {
      notification.error({
        message: err?.response?.data?.message || "Failed to create invoice",
      });
    }
  };

  return (
    <div>
      <PageMeta
        title="Expenses | Art Handler"
        description="Track and manage business expense"
      />

      <BillingSubNav activeKey="expenses" />

      {/* Stats Cards */}
      <Row gutter={[16, 16]} className="mb-6">
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Total Expenses"
              value={totalExpenses}
              precision={2}
              valueStyle={{ color: "#cf1322" }}
              prefix={<DollarOutlined />}
              formatter={(value) => `$${Number(value).toFixed(2)}`}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Pending"
              value={pendingExpenses}
              precision={2}
              valueStyle={{ color: "#faad14" }}
              prefix={<FileTextOutlined />}
              formatter={(value) => `$${Number(value).toFixed(2)}`}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Approved"
              value={approvedExpenses}
              precision={2}
              valueStyle={{ color: "#1890ff" }}
              prefix={<ToolOutlined />}
              formatter={(value) => `$${Number(value).toFixed(2)}`}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Paid"
              value={paidExpenses}
              precision={2}
              valueStyle={{ color: "#3f8600" }}
              prefix={<ShoppingCartOutlined />}
              formatter={(value) => `$${Number(value).toFixed(2)}`}
            />
          </Card>
        </Col>
      </Row>

      {/* Controls */}
      <Card className="mb-6 shadow-sm">
        <Row gutter={16} align="middle">
          <Col flex="auto">
            <Input
              placeholder="Search expenses..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className="max-w-md"
            />
          </Col>
          <Col>
            <Select
              value={statusFilter}
              onChange={setStatusFilter}
              className="w-32"
              suffixIcon={<FilterOutlined />}
            >
              {STATUS_OPTIONS.map((opt) => (
                <Option key={opt.value} value={opt.value}>
                  {opt.label}
                </Option>
              ))}
            </Select>
          </Col>
          <Col>
            <Select
              value={categoryFilter}
              onChange={setCategoryFilter}
              className="w-40"
            >
              <Option value="all">All Categories</Option>
              {CATEGORY_OPTIONS.map((opt) => (
                <Option key={opt.value} value={opt.value}>
                  {opt.label}
                </Option>
              ))}
            </Select>
          </Col>
          <Col>
            <RangePicker
              className="w-64"
              value={dateRange}
              onChange={setDateRange}
            />
          </Col>
          <Col>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setIsModalVisible(true)}
              className="bg-gradient-to-r from-blue-500 to-blue-600 border-0 shadow-md"
            >
              Add Expense
            </Button>
          </Col>
          <Col>
            <Button
              icon={<DollarOutlined />}
              onClick={() => navigate("/billing/expenses/invoices")}
              className="border-gray-300 hover:bg-gray-50"
            >
              View Invoices
            </Button>
          </Col>
        </Row>
      </Card>

      {/* Expenses Table */}
      <Card className="shadow-sm">
        <Table
          columns={columns}
          dataSource={expensesData?.items || []}
          loading={isLoading}
          rowKey="id"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} expenses`,
            total: expensesData?.meta?.totalItems ?? 0,
          }}
          locale={{
            emptyText:
              "No expenses found. Add your first expense to get started.",
          }}
          className="expenses-table"
        />
      </Card>

      {/* Create Expense Modal */}
      <Modal
        title={
          <div className="flex items-center">
            <DollarOutlined className="mr-2 text-blue-500" />
            Add New Expense
          </div>
        }
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleCreateExpense}
          className="mt-4"
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="date"
                label="Date"
                rules={[{ required: true, message: "Please select date" }]}
              >
                <DatePicker style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="amount"
                label="Amount"
                rules={[{ required: true, message: "Please enter amount" }]}
              >
                <InputNumber
                  style={{ width: "100%" }}
                  prefix="$"
                  precision={2}
                  min={0}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="vendorId" label="Vendor">
                <Select
                  showSearch
                  placeholder="Select vendor"
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    String(option?.children)
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                >
                  {vendors.map((vendor) => (
                    <Option key={vendor.id} value={vendor.id}>
                      {vendor.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="category"
                label="Category"
                rules={[{ required: true, message: "Please select category" }]}
              >
                <Select>
                  {CATEGORY_OPTIONS.map((opt) => (
                    <Option key={opt.value} value={opt.value}>
                      {opt.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="description"
            label="Description"
            rules={[{ required: true, message: "Please enter description" }]}
          >
            <TextArea rows={3} />
          </Form.Item>

          <Form.Item name="projectId" label="Project (Optional)">
            <Select
              showSearch
              placeholder="Select project"
              optionFilterProp="children"
              filterOption={(input, option) =>
                String(option?.children)
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            >
              {projects.map((project) => (
                <Option key={project.id} value={project.id}>
                  {project.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          {/* TODO: Upload receipt */}

          <Divider />

          <Form.Item className="mb-0">
            <Space className="w-full justify-end">
              <Button onClick={() => setIsModalVisible(false)}>Cancel</Button>
              <Button
                type="primary"
                htmlType="submit"
                className="bg-blue-500 hover:bg-blue-600"
                loading={createExpenseMutation.isPending}
              >
                Create Expense
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal View Details */}
      <Modal
        title="Expense Details"
        open={isDetailModalOpen}
        onCancel={() => setIsDetailModalOpen(false)}
        footer={null}
        width={500}
      >
        {selectedExpense && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="text-lg font-semibold">
                <DollarOutlined className="mr-2 text-blue-500" />$
                {Number(selectedExpense.amount).toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                })}
              </div>
              <Tag
                color={
                  selectedExpense.status === "paid"
                    ? "green"
                    : selectedExpense.status === "approved"
                    ? "blue"
                    : selectedExpense.status === "pending"
                    ? "orange"
                    : "red"
                }
                className="text-base"
              >
                {selectedExpense.status?.charAt(0).toUpperCase() +
                  selectedExpense.status?.slice(1)}
              </Tag>
            </div>
            <Divider className="my-2" />
            <div className="grid grid-cols-2 gap-x-4 gap-y-2">
              <div className="text-gray-500">Date:</div>
              <div>{selectedExpense.date}</div>
              <div className="text-gray-500">Vendor:</div>
              <div>{selectedExpense.vendor?.name || "-"}</div>
              <div className="text-gray-500">Category:</div>
              <div>{selectedExpense.category}</div>
              <div className="text-gray-500">Project:</div>
              <div>{selectedExpense.project?.name || "-"}</div>
            </div>
            <Divider className="my-2" />
            <div>
              <div className="text-gray-500 mb-1">Description:</div>
              <div>{selectedExpense.description}</div>
            </div>
            {selectedExpense.notes && (
              <div>
                <div className="text-gray-500 mb-1">Notes:</div>
                <div>{selectedExpense.notes}</div>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Modal Edit Expense */}
      <Modal
        title="Edit Expense"
        open={isEditModalOpen}
        onCancel={() => setIsEditModalOpen(false)}
        footer={null}
        width={600}
      >
        {selectedExpense && (
          <Form
            layout="vertical"
            initialValues={{
              ...selectedExpense,
              date: selectedExpense.date
                ? dayjs(selectedExpense.date)
                : undefined,
              amount: Number(selectedExpense.amount),
            }}
            onFinish={async (values) => {
              try {
                await updateExpenseMutation.mutateAsync({
                  expenseId: selectedExpense.id,
                  payload: {
                    ...values,
                    amount: Number(values.amount),
                    date: values.date.format("YYYY-MM-DD"),
                  },
                });
                notification.success({
                  message: "Expense updated successfully!",
                });
                setIsEditModalOpen(false);
              } catch (err: any) {
                notification.error({
                  message:
                    err?.response?.data?.message || "Failed to update expense",
                });
              }
            }}
          >
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="date"
                  label="Date"
                  rules={[{ required: true, message: "Please select date" }]}
                >
                  <DatePicker style={{ width: "100%" }} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="amount"
                  label="Amount"
                  rules={[{ required: true, message: "Please enter amount" }]}
                >
                  <InputNumber
                    style={{ width: "100%" }}
                    prefix="$"
                    precision={2}
                    min={0}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="vendorId" label="Vendor">
                  <Select
                    showSearch
                    placeholder="Select vendor"
                    optionFilterProp="children"
                    filterOption={(input, option) =>
                      String(option?.children)
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                  >
                    {vendors.map((vendor) => (
                      <Option key={vendor.id} value={vendor.id}>
                        {vendor.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="category"
                  label="Category"
                  rules={[
                    { required: true, message: "Please select category" },
                  ]}
                >
                  <Select>
                    {CATEGORY_OPTIONS.map((opt) => (
                      <Option key={opt.value} value={opt.value}>
                        {opt.label}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>
            <Form.Item
              name="description"
              label="Description"
              rules={[{ required: true, message: "Please enter description" }]}
            >
              <TextArea rows={3} />
            </Form.Item>
            <Form.Item name="projectId" label="Project (Optional)">
              <Select
                showSearch
                placeholder="Select project"
                optionFilterProp="children"
                filterOption={(input, option) =>
                  String(option?.children)
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
              >
                {projects.map((project) => (
                  <Option key={project.id} value={project.id}>
                    {project.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item name="notes" label="Notes">
              <TextArea rows={2} />
            </Form.Item>
            <Divider />
            <Form.Item className="mb-0">
              <Space className="w-full justify-end">
                <Button onClick={() => setIsEditModalOpen(false)}>
                  Cancel
                </Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  className="bg-blue-500 hover:bg-blue-600"
                  loading={updateExpenseMutation.isPending}
                >
                  Save Changes
                </Button>
              </Space>
            </Form.Item>
          </Form>
        )}
      </Modal>

      {/* Modal Confirm Delete */}
      <Modal
        title="Delete Expense"
        open={isDeleteConfirmOpen}
        onCancel={() => setIsDeleteConfirmOpen(false)}
        onOk={async () => {
          if (selectedExpense) {
            try {
              await deleteExpenseMutation.mutateAsync(selectedExpense.id);
              notification.success({
                message: "Expense deleted successfully!",
              });
              setIsDeleteConfirmOpen(false);
            } catch (err: any) {
              notification.error({
                message:
                  err?.response?.data?.message || "Failed to delete expense",
              });
            }
          }
        }}
        okText="Delete"
        okButtonProps={{ danger: true }}
        cancelText="Cancel"
        width={400}
      >
        <div>Are you sure you want to delete this expense?</div>
      </Modal>

      {/* Modal Create Invoice */}
      <Modal
        title={
          <div className="flex items-center">
            <DollarOutlined className="mr-2 text-blue-500" />
            Create Invoice for Expense
          </div>
        }
        open={isCreateInvoiceModalOpen}
        onCancel={() => setIsCreateInvoiceModalOpen(false)}
        footer={null}
        width={600}
      >
        {selectedExpense && (
          <div>
            {/* Expense Summary */}
            <Card className="mb-4" size="small">
              <div className="flex items-center justify-between mb-2">
                <div className="text-lg font-semibold">
                  {selectedExpense.expenseRef}
                </div>
                <Tag color="blue">
                  ${Number(selectedExpense.amount).toFixed(2)}
                </Tag>
              </div>
              <div className="text-sm text-gray-600 mb-1">
                <strong>Category:</strong> {selectedExpense.category}
              </div>
              <div className="text-sm text-gray-600 mb-1">
                <strong>Description:</strong> {selectedExpense.description}
              </div>
              {selectedExpense.vendor && (
                <div className="text-sm text-gray-600 mb-1">
                  <strong>Vendor:</strong> {selectedExpense.vendor.name}
                </div>
              )}
              {selectedExpense.project && (
                <div className="text-sm text-gray-600">
                  <strong>Project:</strong> {selectedExpense.project.name}
                </div>
              )}
            </Card>
            <Form
              form={invoiceForm}
              layout="vertical"
              onFinish={handleCreateInvoice}
              className="mt-4"
            >
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="invoiceNumber"
                    label="Invoice Number"
                    rules={[
                      {
                        required: true,
                        message: "Please enter invoice number",
                      },
                    ]}
                  >
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="dueDate"
                    label="Due Date"
                    rules={[
                      { required: true, message: "Please select due date" },
                    ]}
                  >
                    <DatePicker style={{ width: "100%" }} />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item
                name="memo"
                label="Memo"
                rules={[{ required: true, message: "Please enter memo" }]}
              >
                <TextArea rows={3} />
              </Form.Item>
              <Divider />
              <Form.Item className="mb-0">
                <Space className="w-full justify-end">
                  <Button onClick={() => setIsCreateInvoiceModalOpen(false)}>
                    Cancel
                  </Button>
                  <Button
                    type="primary"
                    htmlType="submit"
                    className="bg-blue-500 hover:bg-blue-600"
                    loading={createExpenseInvoiceMutation.isPending}
                  >
                    Create Invoice
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default ScreenExpenses;
